import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, CircleAlert, MapPin, RefreshCw, Search, Shield } from 'lucide-react';
import CarePageHeader from '@/components/CarePageHeader';
import { Button } from '@/components/ui/button';
import { getPatientProfile } from '@/utils/patientProfile';
import { apiUrl } from '@/config/api';
import {
  AGE_GROUPS,
  CANCER_TYPES,
  COMPANIONS,
  DESTINATIONS,
  INSURANCE_TYPES,
  LODGING_NEEDS,
  RELATIONS,
  STAGES,
  URGENCY,
  US_CITIES,
  US_STATES,
  blankIntake,
  buildJourneyPlans,
  formatUSD,
  missingIntakeFields,
  optionLabel,
  prefillFromProfile,
  saveJourneyResult,
  searchOptions,
  loadJourneyResult,
} from '@/lib/journeyBuilder';

function SearchField({
  label,
  value,
  onChange,
  options,
  placeholder,
  testId,
  missing,
  displayValue,
  onPick,
  allowCustom = false,
  wide = false,
}) {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [typed, setTyped] = useState(false);
  const [active, setActive] = useState(0);
  const selected = options.find((row) => row.id === value);
  const currentLabel = displayValue || selected?.label || (allowCustom ? value : '') || '';
  const shown = open && typed ? query : currentLabel;
  const haystack = open && typed ? query : '';
  const matches = useMemo(() => {
    const found = searchOptions(options, haystack);
    return found.slice(0, haystack ? 12 : 8);
  }, [options, haystack]);
  const moreCount = Math.max(0, searchOptions(options, haystack).length - matches.length);

  useEffect(() => {
    setActive(0);
  }, [haystack, open]);

  useEffect(() => {
    const onDoc = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery('');
        setTyped(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const choose = (row) => {
    if (onPick) onPick(row);
    else onChange(row.id);
    setOpen(false);
    setQuery('');
    setTyped(false);
  };

  const close = () => {
    setOpen(false);
    setQuery('');
    setTyped(false);
  };

  return (
    <div className={`jb-field${missing ? ' is-missing' : ''}${wide ? ' is-wide' : ''}${currentLabel ? ' is-filled' : ''}`} ref={wrapRef}>
      <span className="jb-field-label">{label}</span>
      <div className={`jb-search${open ? ' is-open' : ''}`}>
        <Search className="jb-search-icon" aria-hidden="true" />
        <input
          ref={inputRef}
          data-testid={testId}
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={`${testId}-list`}
          role="combobox"
          autoComplete="off"
          value={shown}
          placeholder={placeholder}
          onFocus={() => {
            setOpen(true);
            setTyped(false);
            setQuery('');
          }}
          onChange={(event) => {
            const next = event.target.value;
            setQuery(next);
            setTyped(true);
            setOpen(true);
            if (allowCustom) onChange(next);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setActive((i) => (matches.length ? (i + 1) % matches.length : 0));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
              setActive((i) => (matches.length ? (i - 1 + matches.length) % matches.length : 0));
            } else if (event.key === 'Enter') {
              if (open && matches[active]) {
                event.preventDefault();
                choose(matches[active]);
              }
            } else if (event.key === 'Escape') {
              event.preventDefault();
              close();
              inputRef.current?.blur();
            }
          }}
        />
        <ChevronDown className={`jb-search-caret${open ? ' is-open' : ''}`} aria-hidden="true" />
        {open ? (
          <ul className="jb-suggest" role="listbox" id={`${testId}-list`}>
            {matches.length ? matches.map((row, index) => {
              const isSelected = row.id === value || row.label === currentLabel;
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`${index === active ? 'is-active' : ''}${isSelected ? ' is-selected' : ''}`}
                    onMouseEnter={() => setActive(index)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      choose(row);
                    }}
                  >
                    <span>{row.label}</span>
                    {isSelected ? <Check className="jb-suggest-check" aria-hidden="true" /> : null}
                  </button>
                </li>
              );
            }) : (
              <li className="jb-suggest-empty">{allowCustom ? 'No exact match, you can keep this city' : 'No matches. Try another spelling.'}</li>
            )}
            {moreCount > 0 ? (
              <li className="jb-suggest-more">Type to narrow {moreCount} more</li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

function PlanCard({ plan, recommended, onSelect }) {
  return (
    <article
      className={`flex flex-col rounded-[28px] border bg-paper overflow-hidden ${
        recommended ? 'border-forest-ink shadow-[0_12px_40px_rgba(15,62,23,0.08)]' : 'border-hair'
      }`}
    >
      <div className={`p-6 border-b border-hair ${recommended ? 'bg-mist' : 'bg-paper'}`}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium bg-paper text-forest-ink border border-hair">
            Care package · {plan.planType} plan
          </span>
          {recommended ? (
            <span className="text-[11px] font-semibold text-forest-ink">Recommended start</span>
          ) : null}
        </div>
        <h3 className="text-xl font-semibold text-forest-ink leading-snug">{plan.hospitalName}</h3>
        <p className="text-sm text-charcoal mt-2 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          {plan.city}, {plan.state}
        </p>
        <p className="text-sm text-charcoal mt-2 leading-relaxed">{plan.designation}</p>
      </div>

      <div className="p-6 flex-1 flex flex-col gap-5">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-charcoal font-medium">Itemized estimate band</p>
          <p className="text-[1.65rem] font-semibold text-forest-ink mt-2 tracking-tight">{plan.priceRange}</p>
          <p className="text-sm text-charcoal mt-1">Your share · travel · lodging · {plan.duration}</p>
        </div>
        <dl className="grid grid-cols-1 gap-3 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-charcoal">Treatment estimate (your share)</dt>
            <dd className="font-medium text-forest-ink">{plan.patientOOP}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-charcoal">Transfers / travel</dt>
            <dd className="font-medium text-forest-ink">{plan.travelCost}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-charcoal">Lodging</dt>
            <dd className="font-medium text-forest-ink">{plan.lodgingCost ? formatUSD(plan.lodgingCost) : 'None'}</dd>
          </div>
          <div className="flex justify-between gap-3 pt-3 border-t border-hair">
            <dt className="text-charcoal">Typical insurer allowed</dt>
            <dd className="font-medium text-forest-ink">{plan.medicalAllowed}</dd>
          </div>
        </dl>
        <div className="rounded-2xl border border-hair bg-mist/60 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wide text-charcoal font-medium mb-2">Usually included</p>
          <ul className="space-y-1.5 text-sm text-forest-ink">
            <li>Treatment estimate band for the modeled pathway</li>
            <li>Lodging assumptions for the stay window</li>
            <li>Travel / transfer estimate</li>
            <li>ByOnco coordinator handoff after you request the package</li>
          </ul>
        </div>
        <p className="text-sm text-charcoal leading-relaxed">{plan.hospitalNote}</p>
        <ul className="space-y-2.5">
          {plan.bullets.slice(0, 4).map((bullet) => (
            <li key={bullet} className="flex items-start gap-2.5 text-sm text-forest-ink">
              <Check className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-charcoal leading-relaxed rounded-xl border border-hair bg-paper px-3 py-2">
          Estimates from published market data, not live hospital quotes.
        </p>
        <p className="text-sm text-charcoal italic mt-auto pt-2">{plan.fitNote}</p>
      </div>
      <div className="p-6 pt-0">
        <Button
          type="button"
          onClick={() => onSelect(plan)}
          className="w-full h-12 rounded-full bg-forest-ink text-cream-paper hover:bg-forest-shadow"
        >
          Review {plan.planType.toLowerCase()} package
        </Button>
      </div>
    </article>
  );
}

const CITY_OPTIONS = US_CITIES.map((row) => ({
  id: `${row.city}|${row.state}`,
  label: `${row.city}, ${row.state}`,
  city: row.city,
  state: row.state,
}));

export default function JourneyBuilderPage() {
  const navigate = useNavigate();
  const resultsRef = useRef(null);
  const [intake, setIntake] = useState(() => {
    const saved = loadJourneyResult();
    if (saved?.intake) return { ...blankIntake(), ...saved.intake };
    return prefillFromProfile(getPatientProfile());
  });
  const [result, setResult] = useState(() => {
    const saved = loadJourneyResult();
    return saved?.plans?.length ? saved : null;
  });
  const [error, setError] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const setField = (key, value) => {
    setIntake((prev) => ({ ...prev, [key]: value }));
    setError('');
  };

  const isMissing = (key) => attempted && !String(intake[key] || '').trim();

  const liveSentence = useMemo(() => {
    const who = optionLabel(RELATIONS, intake.relation);
    const cancer = optionLabel(CANCER_TYPES, intake.cancerType);
    const stage = optionLabel(STAGES, intake.stage);
    const cover = optionLabel(INSURANCE_TYPES, intake.insurance);
    if (!who || !cancer || !stage) return 'Start with who this is for, the diagnosis, and the stage.';
    return `Planning care for ${who} with ${stage} ${cancer}${cover ? `, ${cover}` : ''}.`;
  }, [intake]);

  const persistAndShow = (next) => {
    saveJourneyResult(next);
    setResult(next);
    window.requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleBuild = async (event) => {
    event.preventDefault();
    setAttempted(true);
    const gaps = missingIntakeFields(intake);
    if (gaps.length) {
      setError(`Still needed: ${gaps.join(', ')}.`);
      return;
    }
    const local = buildJourneyPlans(intake);
    if (!local.ok) {
      setError(local.error);
      return;
    }
    persistAndShow(local);

    setSyncing(true);
    try {
      const res = await fetch(apiUrl('/api/journey-builder/plans'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('byonco_jwt')
            ? { Authorization: `Bearer ${localStorage.getItem('byonco_jwt')}` }
            : {}),
        },
        body: JSON.stringify(intake),
      });
      if (res.ok) {
        const remote = await res.json();
        if (remote?.plans?.length === 3) {
          persistAndShow({ ...local, ...remote, intake, ok: true });
        }
      }
    } catch {
      /* Local engine already rendered, API sync is best-effort. */
    } finally {
      setSyncing(false);
    }
  };

  const handleReset = () => {
    setIntake(blankIntake());
    setResult(null);
    setError('');
    setAttempted(false);
    try {
      sessionStorage.removeItem('byonco_journey_last');
    } catch {
      /* ignore */
    }
  };

  const openPlan = (plan) => {
    const payload = { ...result, selectedPlan: plan };
    saveJourneyResult(payload);
    navigate(`/journey-builder/plan/${plan.planType.toLowerCase()}`, { state: payload });
  };

  const cityDisplay = intake.city
    ? (intake.state ? `${intake.city}${US_CITIES.some((row) => row.city === intake.city && row.state === intake.state) ? `, ${intake.state}` : ''}` : intake.city)
    : '';

  return (
    <div className="care-page jb-page min-h-screen bg-cream-paper pt-10 sm:pt-14 pb-20 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <CarePageHeader
          current="/journey-builder"
          title="Build a care package"
          lede="Tell us the situation. We return three planning packages, treatment share, lodging, transfers, and a coordinator handoff. These are estimate bands grounded in published sources, not live hospital quotes."
        />

        <form onSubmit={handleBuild} className="jb-sheet">
          <p className="jb-summary">{liveSentence}</p>

          <section className="jb-section">
            <p className="jb-section-title"><span>01</span> The patient</p>
            <div className="jb-grid">
              <SearchField label="Who is this for?" value={intake.relation} onChange={(v) => setField('relation', v)} options={RELATIONS} placeholder="Search myself, spouse, parent…" testId="jb-relation" missing={isMissing('relation')} />
              <SearchField label="The patient is" value={intake.ageGroup} onChange={(v) => setField('ageGroup', v)} options={AGE_GROUPS} placeholder="Search adult or child" testId="jb-age" missing={isMissing('ageGroup')} />
              <SearchField label="Diagnosis" value={intake.cancerType} onChange={(v) => setField('cancerType', v)} options={CANCER_TYPES} placeholder="Search a cancer type" testId="jb-cancer" missing={isMissing('cancerType')} />
              <SearchField label="Stage" value={intake.stage} onChange={(v) => setField('stage', v)} options={STAGES} placeholder="Search a stage" testId="jb-stage" missing={isMissing('stage')} />
            </div>
          </section>

          <section className="jb-section">
            <p className="jb-section-title"><span>02</span> Where you are</p>
            <div className="jb-grid">
              <SearchField
                label="Home city"
                value={intake.city}
                displayValue={cityDisplay}
                allowCustom
                onChange={(v) => setField('city', v.split(',')[0].trim())}
                onPick={(row) => {
                  setError('');
                  setIntake((prev) => ({ ...prev, city: row.city, state: row.state }));
                }}
                options={CITY_OPTIONS}
                placeholder="Type a city, New York, Houston, Austin…"
                testId="jb-city"
                missing={isMissing('city')}
              />
              <SearchField
                label="State"
                value={intake.state}
                onChange={(v) => setField('state', v)}
                options={US_STATES}
                placeholder="Search a state"
                testId="jb-state"
                missing={isMissing('state')}
              />
              <SearchField
                wide
                label="Where should treatment happen?"
                value={intake.destination}
                onChange={(v) => setField('destination', v)}
                options={DESTINATIONS}
                placeholder="Search a city, or choose nearby / best-fit"
                testId="jb-destination"
                missing={isMissing('destination')}
              />
            </div>
          </section>

          <section className="jb-section">
            <p className="jb-section-title"><span>03</span> Coverage and stay</p>
            <div className="jb-grid">
              <SearchField label="Coverage" value={intake.insurance} onChange={(v) => setField('insurance', v)} options={INSURANCE_TYPES} placeholder="Search employer PPO, Medicare, Medicaid…" testId="jb-insurance" missing={isMissing('insurance')} />
              <SearchField label="Housing during treatment" value={intake.lodging} onChange={(v) => setField('lodging', v)} options={LODGING_NEEDS} placeholder="Search housing options" testId="jb-lodging" missing={isMissing('lodging')} />
              <SearchField label="Who is traveling?" value={intake.companions} onChange={(v) => setField('companions', v)} options={COMPANIONS} placeholder="Patient only, or with a caregiver" testId="jb-companions" missing={isMissing('companions')} />
              <SearchField label="Timing" value={intake.urgency} onChange={(v) => setField('urgency', v)} options={URGENCY} placeholder="Search timing" testId="jb-urgency" missing={isMissing('urgency')} />
            </div>
            <label className="jb-field is-wide mt-6 block text-left">
              <span className="jb-field-label">Member ID for eligibility (optional)</span>
              <input
                className="mt-2 w-full h-12 rounded-2xl border border-hair bg-mist px-4 text-forest-ink"
                value={intake.memberId || ''}
                onChange={(e) => setField('memberId', e.target.value)}
                placeholder="Insurance member ID, unlocks live OOP + in-network check"
                autoComplete="off"
                data-testid="jb-member-id"
              />
              <span className="text-xs text-charcoal mt-2 block leading-relaxed">
                We run a benefits-style eligibility check (sandbox now; Stedi live when configured). Not required to see packages.
              </span>
            </label>
          </section>

          {error ? (
            <div className="jb-alert" role="alert">
              <CircleAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <div className="jb-actions">
            <Button
              type="submit"
              className="h-12 px-7 rounded-full bg-forest-ink text-cream-paper hover:bg-forest-shadow"
              data-testid="jb-submit"
            >
              Show my three packages
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <button type="button" onClick={handleReset} className="jb-clear">
              <RefreshCw className="w-4 h-4" />
              Clear blanks
            </button>
            {syncing ? <span className="text-sm text-charcoal">Saving this journey to your account…</span> : null}
          </div>
        </form>

        <div ref={resultsRef} className="scroll-mt-6">
          {result?.plans?.length ? (
            <section className="mt-12 sm:mt-16" data-testid="jb-results">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                <div>
                  <p className="text-xs uppercase tracking-wide text-charcoal font-medium">Your care packages</p>
                  <h2 className="text-2xl font-semibold text-forest-ink mt-2">{result.profile.cancerType} · {result.profile.stage}</h2>
                  <p className="text-sm text-charcoal mt-2">{result.profile.home} · {result.profile.insurance}</p>
                </div>
                <p className="text-sm text-charcoal max-w-md leading-relaxed">{result.summary}</p>
              </div>

              <div className="rounded-2xl border border-hair bg-mist/70 px-5 py-4 mb-8 text-sm text-forest-ink leading-relaxed">
                Protocol we modeled: {result.profile.recommendedProtocol}
                {result.liveMarketplace ? (
                  <span className="block mt-2 text-charcoal">
                    Live marketplace enrichment on, quotes and eligibility applied where available.
                  </span>
                ) : null}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {result.plans.map((plan) => (
                  <PlanCard
                    key={plan.planType}
                    plan={plan}
                    recommended={plan.planType === 'Balanced'}
                    onSelect={openPlan}
                  />
                ))}
              </div>

              {result.suggestions?.items?.length ? (
                <div className="mt-10 rounded-[28px] border border-hair bg-paper p-7">
                  <h3 className="text-base font-semibold text-forest-ink mb-4">Ways to tighten this plan</h3>
                  <ul className="space-y-4">
                    {result.suggestions.items.map((item) => (
                      <li key={item.title} className="text-sm text-forest-ink leading-relaxed">
                        <span className="font-semibold">{item.title}. </span>
                        <span className="text-charcoal">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="mt-8 flex gap-3 rounded-2xl border border-hair bg-paper p-5">
                <Shield className="w-4 h-4 mt-0.5 shrink-0 text-forest-ink" />
                <div className="text-xs text-charcoal leading-relaxed">
                  <p>{result.disclaimer}</p>
                  <p className="mt-2">
                    Sources: {(result.sources || []).map((row) => row.label).join(' · ')}
                  </p>
                </div>
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
